// denseRimPillColumns.js — the rim-pill layout for VERY dense wheels.
//
// stgfix-10191600-t22438 (Gordon 2026-10-19 "holy crap this Wheel looks bad",
// web-stg /builder/509246?segment=6881411&pane=2, 61 segments, Segment
// Settings pane + Build panel open). MEASURED web-stg main.dcbe2e54 @1440x900:
// the general passes in layoutRimPillStack (circular fan, tight stack, residual
// X shift, roomless cascade, tangent order) each move a crowd of 61 pills a
// little, and the sum of their moves leaves the right half in two ragged x
// levels, a left-half pill (Segment 59) inside the right of the left column,
// Segment 33 alone inside the 6 o'clock crowd and Segment 16 under the Build
// panel: 46 of 61 pills off their own wedge, up to 209px from it.
//
// Design (dense wheels only, > DENSE_RIM_PILL_MIN_COUNT pills, Builder room
// with a real top and bottom): no pill is ever hidden; each half of the wheel
// is ONE tidy column, in clockwise-from-12 order (12 -> 6 o'clock down the
// right, 6 -> 12 o'clock up the left). Every pill aims for the height of its
// own wedge's point on an ellipse as wide as the ring and as tall as the column;
// the column keeps its order with neighbours touching
// at `gap` (least-squares fit, pool-adjacent-violators), clamped to the room.
// A column taller than the room shares the shortfall evenly between all its
// neighbours (at most DENSE_RIM_PILL_MAX_SHARE_PX each, inside the pill padding)
// instead of pushing it onto the pole pills. A room too short for that first
// seats the pills nearest 12 / 6 o'clock two to a row, side by side above /
// below the ring and inside the column's own width, until the column fits
// (audit-recheck-EditWheel-2610191933: MEASURED web-stg main.33e30651
// /builder/509246 @1440x800 and 1366x768, pane open — yielding to the general
// passes brought Gordon's scatter back, pills up to 449px from their wedge and
// 7-9 under the Build panel). Only a room too short even for that returns []
// so the general passes lay the wheel out without covering titles. Each
// pill then sits on that ellipse (never closer than requiredGapPx to the ring)
// at its height, so the column follows the wheel's curve and the two 12 / 6
// o'clock pills sit side by side on the wheel's axis. A pill that would land on a canvas control or past the
// room's side is pulled back inside the room on its own row (never onto the
// ring). Position-independent: the result depends only on the angles, sizes,
// ring and room, so the layout's second run lands the pills in the same place.
//
// Pure. pills: [{ id, midAngle (0 = 12 o'clock, clockwise), left, right, top,
// bottom }] (page px, current boxes). Returns [{ id, dx, dy }] from those
// boxes, or [] when the input is not a dense wheel in a bounded room.

export const DENSE_RIM_PILL_MIN_COUNT = 37;
export const DENSE_RIM_PILL_GAP_PX = 2;
// T22.131 guard (audit-recheck-Other-2610191755): the most a column may share
// its room's shortfall as overlap between neighbours — inside a pill's padding,
// never over its title. web-stg main.6fc955b0 /builder/509246 (61 segments):
// 1440x900 needs 2.19px per pair (kept); 1366x768 needed 6.24px and 1280x720
// 7.7px (every pill shingled over the one above), so a room that short yields
// to the general passes instead.
export const DENSE_RIM_PILL_MAX_SHARE_PX = 3;

const TAU = 2 * Math.PI;
const norm = (a) => ((a % TAU) + TAU) % TAU;

export function isDenseRimPillCrowd(count, room) {
  return (
    count >= DENSE_RIM_PILL_MIN_COUNT &&
    !!room &&
    Number.isFinite(room.minTop) &&
    Number.isFinite(room.maxBottom) &&
    room.maxBottom > room.minTop
  );
}

// Least-squares non-decreasing fit (pool-adjacent-violators), then clamp.
function isotonic(z, lo, hi) {
  const blocks = [];
  z.forEach((v, k) => {
    blocks.push({ start: k, end: k, sum: v, count: 1 });
    while (blocks.length > 1) {
      const b = blocks[blocks.length - 1];
      const a = blocks[blocks.length - 2];
      if (a.sum / a.count <= b.sum / b.count) break;
      blocks.splice(-2, 2, {
        start: a.start,
        end: b.end,
        sum: a.sum + b.sum,
        count: a.count + b.count,
      });
    }
  });
  const out = new Array(z.length);
  for (const b of blocks) {
    const v = Math.min(hi, Math.max(lo, b.sum / b.count));
    for (let k = b.start; k <= b.end; k++) out[k] = v;
  }
  return out;
}

export function resolveDenseRimPillColumns(
  pills,
  {
    center,
    radius,
    requiredGapPx = 0,
    gap = DENSE_RIM_PILL_GAP_PX,
    minTop,
    maxBottom,
    minX = -Infinity,
    maxX = Infinity,
    obstacles = [],
  } = {},
) {
  if (
    !center ||
    !Number.isFinite(center.x) ||
    !Number.isFinite(center.y) ||
    !(radius > 0)
  )
    return [];
  const valid = (pills || []).filter(
    (p) =>
      p &&
      Number.isFinite(p.midAngle) &&
      [p.left, p.right, p.top, p.bottom].every(Number.isFinite) &&
      p.right > p.left &&
      p.bottom > p.top,
  );
  if (!isDenseRimPillCrowd(valid.length, { minTop, maxBottom })) return [];
  const clearR = radius + Math.max(0, requiredGapPx);
  const rightCol = valid
    .filter((p) => norm(p.midAngle) < Math.PI)
    .sort((a, b) => norm(a.midAngle) - norm(b.midAngle));
  const leftCol = valid
    .filter((p) => norm(p.midAngle) >= Math.PI)
    .sort((a, b) => norm(b.midAngle) - norm(a.midAngle));
  const hits = (a, o) =>
    a.left < o.right &&
    a.right > o.left &&
    a.top < o.bottom &&
    a.bottom > o.top;
  const ringClearX = (top, bottom) => {
    const dy =
      top > center.y
        ? top - center.y
        : bottom < center.y
          ? center.y - bottom
          : 0;
    return dy >= clearR ? 0 : Math.sqrt(clearR * clearR - dy * dy);
  };
  // Lays one half out with `pairs` two-pill rows at its ends (T22.438
  // short room, header); null when that many pairs still shingle, false when
  // a pair cannot sit beside the ring inside the column's own width.
  const layoutSide = (col, side, pairs) => {
    const rows = col.map((p, k) => [k]);
    for (let q = 0; q < pairs; q++) {
      // Alternate the 12 and 6 o'clock ends; the pill nearer the pole sits
      // nearer the wheel's axis so the pair still reads clockwise.
      const t = Math.floor(q / 2);
      if (q % 2 === 0) rows.splice(t, 2, [rows[t][0], rows[t + 1][0]]);
      else {
        const b = rows.length - 1 - t;
        rows.splice(b - 1, 2, [rows[b][0], rows[b - 1][0]]);
      }
    }
    const m = rows.length;
    const h = rows.map((r) => Math.max(...r.map((k) => col[k].bottom - col[k].top)));
    const spacing = [0];
    for (let k = 1; k < m; k++)
      spacing.push(spacing[k - 1] + (h[k - 1] + h[k]) / 2 + gap);
    const lo = minTop + h[0] / 2;
    let hi = maxBottom - h[m - 1] / 2 - spacing[m - 1];
    if (hi < lo) {
      // Taller than the room: every neighbour pair gives up the same share.
      const fit =
        m > 1
          ? Math.max(
              0,
              (maxBottom - minTop - h[0] / 2 - h[m - 1] / 2) / spacing[m - 1],
            )
          : 1;
      for (let k = 0; k < m; k++) spacing[k] *= fit;
      for (let k = 1; k < m; k++)
        if (
          (h[k - 1] + h[k]) / 2 - (spacing[k] - spacing[k - 1]) >
          DENSE_RIM_PILL_MAX_SHARE_PX
        )
          return null;
      hi = lo;
    }
    // The column follows an ellipse round the ring: as wide as the ring
    // (plus its gap), as tall as the column (at most the room) — so a pill keeps its wedge's
    // direction instead of the poles' crowd queuing straight above / below
    // the hub. Each pill aims for its own wedge's point on that ellipse.
    const ry = Math.max(
      clearR,
      Math.min(
        (spacing[m - 1] + (h[0] + h[m - 1]) / 2) / 2,
        Math.max(center.y - minTop, maxBottom - center.y),
      ),
    );
    const target = rows.map(
      (r) =>
        center.y -
        ry * Math.cos(r.reduce((a, k) => a + col[k].midAngle, 0) / r.length),
    );
    const base = isotonic(
      target.map((t, k) => t - spacing[k]),
      lo,
      hi,
    );
    const widest = Math.max(...col.map((p) => p.right - p.left));
    const moves = [];
    for (let k = 0; k < m; k++) {
      const mid = base[k] + spacing[k];
      const top = mid - h[k] / 2;
      const bottom = top + h[k];
      const dyc = mid - center.y;
      const inner = Math.max(
        ringClearX(top, bottom),
        clearR * Math.sqrt(Math.max(0, 1 - (dyc / ry) ** 2)),
      );
      if (rows[k].length === 2) {
        // Side by side, clear of the ring and inside the column's own width
        // (never out under a panel the room does not know about).
        const [a, b] = rows[k].map((j) => col[j]);
        const wa = a.right - a.left;
        const wb = b.right - b.left;
        const extent = Math.min(
          clearR + widest,
          side > 0 ? maxX - center.x : center.x - minX,
        );
        const span = wa + gap + wb;
        const from = Math.max(gap / 2, Math.min(inner, extent - span));
        if (ringClearX(top, bottom) > 0 || from + span > extent + 0.5)
          return false;
        for (const [p, off, w] of [
          [a, from, wa],
          [b, from + wa + gap, wb],
        ]) {
          const left = side > 0 ? center.x + off : center.x - off - w;
          const pt = mid - (p.bottom - p.top) / 2;
          const box = { left, right: left + w, top: pt, bottom: pt + p.bottom - p.top };
          if ((obstacles || []).some((o) => o && hits(box, o))) return false;
          moves.push({ id: p.id, dx: left - p.left, dy: pt - p.top });
        }
        continue;
      }
      const p = col[rows[k][0]];
      const w = p.right - p.left;
      let left = side > 0 ? center.x + inner : center.x - inner - w;
      // Inside the room's sides and off canvas controls, on the same row —
      // but never back onto the ring.
      const clamp = (l) => {
        let v = l;
        if (v + w > maxX) v = maxX - w;
        if (v < minX) v = minX;
        return v;
      };
      const onRing = (l) =>
        side > 0 ? l < center.x + inner - 0.5 : l + w > center.x - inner + 0.5;
      const c = clamp(left);
      if (!onRing(c)) left = c;
      for (const o of obstacles || []) {
        if (!o || !hits({ left, right: left + w, top, bottom }, o)) continue;
        const moved = side > 0 ? o.left - gap - w : o.right + gap;
        if (!onRing(moved) && moved >= minX && moved + w <= maxX) left = moved;
      }
      moves.push({ id: p.id, dx: left - p.left, dy: top - p.top });
    }
    return moves;
  };
  const out = [];
  for (const [col, side] of [
    [rightCol, 1],
    [leftCol, -1],
  ]) {
    if (!col.length) continue;
    // Always try the plain column first: a one-pill half (one segment worth
    // more than half the wheel) has no pair to seat but still fits.
    let moves = layoutSide(col, side, 0);
    for (let pairs = 1; moves === null && 2 * pairs <= col.length - 2; pairs++)
      moves = layoutSide(col, side, pairs);
    if (!moves) return [];
    out.push(...moves);
  }
  return out;
}
